import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Outlet, Navigate } from 'react-router-dom';
import { tokens, GlassPanel, Badge, Card, StatDisplay, Header } from '@ekelhaft-tools/eitelkeit';
import { useAgent, type HealthCheckResult } from './hooks/useAgent';
import { useDashboardStore } from './stores/dashboard';
import { Jobs } from './pages/Jobs';
import { Pipelines } from './pages/Pipelines';
import { PipelineDAG } from './pages/PipelineDAG';
import { Timeline } from './pages/Timeline';
import { StrategyStatus } from './pages/StrategyStatus';
import { Metrics } from './pages/Metrics';
import { Logs } from './pages/Logs';
import { Approvals } from './pages/Approvals';
import { DeploymentDetail } from './pages/DeploymentDetail';
import { Agents } from './pages/Agents';
import { Settings } from './pages/Settings';
import { Artifacts } from './pages/Artifacts';
import { EnvironmentsCrud } from './pages/EnvironmentsCrud';
import { VortexInspectorPage } from './pages/VortexInspectorPage';

// ═══════════════════════════════════════════════════════════════
// NAVIGATION
// ═══════════════════════════════════════════════════════════════

const navLinks = [
  { to: '/overview', label: 'Overview' },
  { to: '/vortex', label: 'Vortex Tunnel' },
  { to: '/agents', label: 'Nodes' },
  { to: '/jobs', label: 'Job Queue' },
  { to: '/pipelines', label: 'Pipelines' },
  { to: '/deployments', label: 'Deployments' },
  { to: '/approvals', label: 'Approvals' },
  { to: '/metrics', label: 'Metrics' },
  { to: '/artifacts', label: 'Inventory' },
  { to: '/environments', label: 'Env Matrix' },
  { to: '/settings', label: 'Settings' },
];

// ═══════════════════════════════════════════════════════════════
// LAYOUT
// ═══════════════════════════════════════════════════════════════

function Layout() {
  const { connectionStatus } = useAgent();

  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: tokens.colors.void,
        color: tokens.colors.textPrimary,
        fontFamily: tokens.fonts.mono,
      }}
    >
      {/* Sidebar */}
      <GlassPanel
        blur="lg"
        intensity="heavy"
        bordered={true}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: tokens.spacing[4],
          padding: tokens.spacing[4],
          width: '220px',
          borderRadius: 0,
          borderRight: `1px solid ${tokens.colors.border}`,
          borderLeft: 'none',
          borderTop: 'none',
          borderBottom: 'none',
        }}
      >
        <div style={{ padding: tokens.spacing[2] }}>
          <h1
            style={{
              fontSize: tokens.fontSizes.xl,
              color: tokens.colors.mint,
              letterSpacing: '0.1em',
              margin: 0,
            }}
          >
            BRUTAL.IPM
          </h1>
          <span style={{ fontSize: tokens.fontSizes.xs, color: tokens.colors.textMuted }}>
            Ouroboros v0.1
          </span>
        </div>

        <nav style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing[1] }}>
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              style={({ isActive }) => ({
                color: isActive ? tokens.colors.mint : tokens.colors.textMuted,
                textDecoration: 'none',
                padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
                borderRadius: tokens.radii.sm,
                backgroundColor: isActive ? 'rgba(0, 255, 135, 0.1)' : 'transparent',
                transition: 'all 0.2s ease',
                fontSize: tokens.fontSizes.sm,
              })}
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div style={{ marginTop: 'auto', padding: tokens.spacing[2] }}>
          <Badge variant={connectionStatus === 'connected' ? 'success' : 'warning'} size="sm">
            {connectionStatus.toUpperCase()}
          </Badge>
        </div>
      </GlassPanel>

      {/* Main Content */}
      <main style={{ flex: 1, padding: tokens.spacing[6], overflowY: 'auto' }}>
        <Header
          title="BRUTAL Operation Center"
          titleIcon={null}
          badge={null}
          actions={null}
          glass={true}
        />
        <div style={{ marginTop: tokens.spacing[6] }}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// OVERVIEW
// ═══════════════════════════════════════════════════════════════

function Overview() {
  const { isConnected, healthCheck, listDeployments } = useAgent();
  const { deployments, setDeployments } = useDashboardStore();
  const [health, setHealth] = useState<HealthCheckResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isConnected) return;

    const fetchData = async () => {
      try {
        const [healthResult, deploymentList] = await Promise.all([
          healthCheck(),
          listDeployments(),
        ]);
        setHealth(healthResult);
        setDeployments(deploymentList);
      } catch (err) {
        console.error('Overview sync failed:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isConnected, healthCheck, listDeployments, setDeployments]);

  if (loading) {
    return (
      <Card variant="glass" padding="md" hover={false}>
        Syncing with Vortex...
      </Card>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing[8] }}>
      <div
        style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: tokens.spacing[4] }}
      >
        <StatDisplay
          label="Daemon Status"
          value={health?.agentStatus ?? 'OFFLINE'}
          trend={null}
          trendValue={null}
          size="md"
        />
        <StatDisplay
          label="Active Jobs"
          value={deployments.filter((d) => d.status === 'running').length.toString()}
          trend={null}
          trendValue={null}
          size="md"
        />
        <StatDisplay label="Ouroboros" value="ACTIVE" trend={null} trendValue={null} size="md" />
        <StatDisplay label="Encryption" value="CHACHA20" trend={null} trendValue={null} size="md" />
      </div>

      <Card title="Live Deployment Stream" variant="glass" padding="lg" hover={true}>
        <div style={{ color: tokens.colors.textMuted }}>Awaiting Maelstrom packets...</div>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// APP
// ═══════════════════════════════════════════════════════════════

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<Layout />}>
          <Route index element={<Navigate to="/overview" replace />} />
          <Route path="overview" element={<Overview />} />
          <Route path="vortex" element={<VortexInspectorPage />} />
          <Route path="agents" element={<Agents />} />
          <Route path="jobs" element={<Jobs />} />
          <Route path="pipelines" element={<Pipelines />} />
          <Route path="pipelines/:id" element={<PipelineDAG />} />
          <Route path="timeline" element={<Timeline />} />
          <Route path="strategy" element={<StrategyStatus />} />
          <Route path="deployments" element={<DeploymentsPage />} />
          <Route path="deployments/:id" element={<DeploymentDetail />} />
          <Route path="approvals" element={<Approvals />} />
          <Route path="logs" element={<Logs />} />
          <Route path="metrics" element={<Metrics />} />
          <Route path="artifacts" element={<Artifacts />} />
          <Route path="env-config" element={<EnvironmentsCrud />} />
          <Route path="environments" element={<EnvironmentsMatrixPage />} />
          <Route path="settings" element={<Settings />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

// Placeholder functions for missing routes to avoid build errors
function DeploymentsPage() {
  return (
    <Card variant="default" padding="md" hover={false}>
      Deployments View
    </Card>
  );
}
function EnvironmentsMatrixPage() {
  return (
    <Card variant="default" padding="md" hover={false}>
      Environment Matrix
    </Card>
  );
}
